import { useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import ApiManager from "../../api/ApiManager";
import Button from "../../components/Button/Button";
import OnboardingLayout from "../../layouts/OnboardingLayout/OnboardingLayout";
import Typography from "../../components/Typography/Typography";
import Input from "../../components/Input/Input";
import { ArrowLeft } from "lucide-react";
import { Toaster, toast } from "react-hot-toast";
import { useAuth } from "../../store/AuthContext/AuthContext";

const api = new ApiManager();

const PasswordRecovery = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // Step 1: Enter email, Step 2: Enter new password with verification code
  const [credentials, setCredentials] = useState({
    email: "",
    verificationCode: "",
    newPassword: "",
  });
  const [error, setError] = useState("");

  const handleChange = useCallback((e) => {
    setCredentials((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const handleSendVerificationCode = (e) => {
    e.preventDefault();
    if (!credentials.email) {
      setError("Inserisci un indirizzo email valido");
      return;
    }

    // API call to send verification code
    api
      .post("/forgot-password", {
        email: credentials.email,
      })
      .then((res) => {
        toast.success("Codice di verifica inviato con successo");
        setStep(2);
      })
      .catch((error) => {
        console.error(error);
        toast.error(
          error.response?.data?.Message || "Errore nell'invio del codice"
        );
      });
  };

  const handleSetNewPassword = (e) => {
    e.preventDefault();
    if (!credentials.newPassword || !credentials.verificationCode) {
      setError("Inserisci tutti i campi richiesti");
      return;
    }

    // API call to set new password
    api
      .post("/confirm-forgot-password", {
        email: credentials.email,
        code: credentials.verificationCode,
        new_password: credentials.newPassword,
      })
      .then((res) => {
        toast.success("Password aggiornata con successo");
        navigate("/login");
      })
      .catch((error) => {
        console.error(error);
        toast.error(
          error.response?.data?.Message || "Errore nel recupero della password"
        );
      });
  };

  const isEmailStepIncomplete = !credentials.email;
  const isPasswordStepIncomplete =
    !credentials.verificationCode || !credentials.newPassword;

  return (
    <OnboardingLayout hasProgress={false}>
      <div className="pt-12 pb-14 md:mx-auto md:w-[40rem] w-auto px-4 flex flex-col gap-4 md:gap-9">
        <Toaster />
        <Button
          onClick={() => (step === 1 ? navigate(-1) : setStep(1))}
          size="md"
          type="bw"
          icon={<ArrowLeft size="24" />}
        />

        {step === 1 ? (
          <>
            <Typography
              family="serif"
              size="32"
              desktopSize="48"
              weight="sb"
              color="black"
            >
              Recupera la tua password
            </Typography>

            {error && <p className="text-red-500">{error}</p>}

            <form
              className="flex flex-col gap-[36px]"
              onSubmit={handleSendVerificationCode}
            >
              <div className="flex flex-col gap-4">
                <Input
                  title="Email"
                  name="email"
                  value={credentials.email}
                  onChange={handleChange}
                />
              </div>
              <Button
                text="Invia il codice di verifica"
                width="full"
                size={window.innerWidth >= 768 ? "lg" : "md"}
                type="color"
                disabled={isEmailStepIncomplete}
              />
            </form>
          </>
        ) : (
          <>
            <Typography
              family="serif"
              size="32"
              desktopSize="48"
              weight="sb"
              color="black"
            >
              Imposta una nuova password
            </Typography>

            {error && <p className="text-red-500">{error}</p>}

            <form
              className="flex flex-col gap-[36px]"
              onSubmit={handleSetNewPassword}
            >
              <div className="flex flex-col gap-4">
                <Input
                  title="Nuova Password"
                  name="newPassword"
                  type="password"
                  placeholder="6+ caratteri"
                  value={credentials.newPassword}
                  onChange={handleChange}
                />
                <Input
                  title="Codice di verifica"
                  name="verificationCode"
                  value={credentials.verificationCode}
                  onChange={handleChange}
                />
              </div>
              <Button
                text="Imposta la nuova password"
                width="full"
                size={window.innerWidth >= 768 ? "lg" : "md"}
                type="color"
                disabled={isPasswordStepIncomplete}
              />
            </form>
          </>
        )}
      </div>
    </OnboardingLayout>
  );
};

export default PasswordRecovery;
